/* eslint-disable no-func-assign */
/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

'use strict';

function _typeof(obj) {
  '@babel/helpers - typeof';
  if (typeof Symbol === 'function' && typeof Symbol.iterator === 'symbol') {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj &&
        typeof Symbol === 'function' &&
        obj.constructor === Symbol &&
        obj !== Symbol.prototype
        ? 'symbol'
        : typeof obj;
    };
  }
  return _typeof(obj);
}

import * as _core from '@emotion/react';

import React from 'react';

import * as _propTypes from 'prop-types';

import * as _theme from '@veneer/theme';

import * as _reactTransitionGroup from 'react-transition-group';

import * as _reactMergeRefs from 'react-merge-refs';

import * as _styles from './styles';

import * as _segmented_control_context from './segmented_control_context';

import * as _use_media_query from '../utils/use_media_query';

import * as _use_controlled from '../utils/use_controlled';

import * as _use_direction from '../direction/use_direction';
import { breakpoints } from '../../../base';

var _excluded = [
  'aria-label',
  'children',
  'defaultItem',
  'id',
  'onChange',
  'selectedItem',
  'theme'
];

function _extends() {
  _extends =
    Object.assign ||
    function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i];
        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key];
          }
        }
      }
      return target;
    };
  return _extends.apply(this, arguments);
}

function _slicedToArray(arr, i) {
  return (
    _arrayWithHoles(arr) ||
    _iterableToArrayLimit(arr, i) ||
    _unsupportedIterableToArray(arr, i) ||
    _nonIterableRest()
  );
}

function _nonIterableRest() {
  throw new TypeError(
    'Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.'
  );
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === 'string') return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === 'Object' && o.constructor) n = o.constructor.name;
  if (n === 'Map' || n === 'Set') return Array.from(o);
  if (n === 'Arguments' || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n))
    return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i =
    arr == null
      ? null
      : (typeof Symbol !== 'undefined' && arr[Symbol.iterator]) ||
        arr['@@iterator'];
  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _s, _e;
  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i['return'] != null) _i['return']();
    } finally {
      // eslint-disable-next-line no-unsafe-finally
      if (_d) throw _e;
    }
  }
  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

var SegmentedControl = /*#__PURE__*/ React.forwardRef(function (props, ref) {
  var ariaLabelProp = props['aria-label'],
    children = props.children,
    defaultItem = props.defaultItem,
    id = props.id,
    onChange = props.onChange,
    selectedItem = props.selectedItem,
    theme = props.theme,
    segmentedControlProps = _objectWithoutProperties(props, _excluded); // refs to control the items

  var segmentRefs = React.useMemo(
    function () {
      return Array(React.Children.count(children))
        .fill(0)
        .map(function () {
          return /*#__PURE__*/ React.createRef();
        });
    },
    [children]
  );

  var _useControlled = (0, _use_controlled.default)({
      controlled: segmentRefs[selectedItem],
      default: segmentRefs[defaultItem || 0]
    }),
    _useControlled2 = _slicedToArray(_useControlled, 2),
    activeSegment = _useControlled2[0],
    setActiveSegment = _useControlled2[1];

  var _React$useState = React.useState({
      radioWidth: undefined,
      radioOffset: undefined
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    animation = _React$useState2[0],
    setAnimation = _React$useState2[1];

  var shouldAnimate = React.useRef(true);
  var rtlRef = React.useRef();

  var _useDirection = (0, _use_direction.default)(),
    isRTL = _useDirection.isRTL;

  var isMobile = (0, _use_media_query.default)(
    '(max-width: '.concat(breakpoints.md, ')')
  ); // sets the width and offset of the active background

  React.useEffect(
    function () {
      var _rtlRef$current;

      var radio =
        activeSegment === null || activeSegment === void 0
          ? void 0
          : activeSegment.current;
      var horizontalOffset = isRTL
        ? (rtlRef === null || rtlRef === void 0
            ? void 0
            : (_rtlRef$current = rtlRef.current) === null ||
                _rtlRef$current === void 0
              ? void 0
              : _rtlRef$current.offsetWidth) -
          (radio === null || radio === void 0 ? void 0 : radio.offsetLeft) -
          (radio === null || radio === void 0 ? void 0 : radio.offsetWidth)
        : radio === null || radio === void 0
          ? void 0
          : radio.offsetLeft;
      setAnimation({
        radioSize: isMobile
          ? radio === null || radio === void 0
            ? void 0
            : radio.offsetHeight
          : radio === null || radio === void 0
            ? void 0
            : radio.offsetWidth,
        radioOffset: isMobile
          ? radio === null || radio === void 0
            ? void 0
            : radio.offsetTop
          : horizontalOffset
      });
    },
    [activeSegment, isMobile, isRTL, setAnimation]
  ); // first render animation

  React.useEffect(function () {
    shouldAnimate.current = false;
  }, []);
  var style = React.useMemo(
    function () {
      return (0, _styles.default)({
        radioOffset: animation.radioOffset,
        radioSize: animation.radioSize,
        shouldAnimate: shouldAnimate.current,
        theme: theme
      });
    },
    [animation.radioOffset, animation.radioSize, shouldAnimate, theme]
  );
  var contextValue = React.useMemo(
    function () {
      return {
        activeSegment: activeSegment,
        isMobile: isMobile,
        onChange: onChange,
        segmentRefs: segmentRefs,
        selectedItem: selectedItem,
        setActiveSegment: setActiveSegment,
        shouldAnimate: shouldAnimate,
        theme: theme
      };
    },
    [
      activeSegment,
      isMobile,
      onChange,
      segmentRefs,
      selectedItem,
      setActiveSegment,
      shouldAnimate,
      theme
    ]
  );
  var handleRef = (0, _reactMergeRefs.default)([rtlRef, ref]);
  return (0, _core.jsx)(
    _segmented_control_context.default.Provider,
    {
      value: contextValue
    },
    (0, _core.jsx)(
      'div',
      _extends({}, segmentedControlProps, {
        'aria-label': ariaLabelProp || null,
        css: /*#__PURE__*/ (0, _core.css)(
          [style.controls, isMobile && style.mobile],
          process.env.NODE_ENV === 'production'
            ? ''
            : '/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */'
        ),
        id: id,
        ref: handleRef,
        role: 'radiogroup'
      }),
      (0, _core.jsx)(
        _reactTransitionGroup.TransitionGroup,
        {
          appear: false,
          component: null
        },
        React.Children.map(children, function (child, i) {
          return /*#__PURE__*/ React.cloneElement(child, {
            componentRef: segmentRefs[i],
            index: i
          });
        })
      )
    )
  );
});
SegmentedControl.propTypes = {
  /**
   * Labels the current element.
   */
  'aria-label': _propTypes.default.string,

  /**
   * Expects `<SegmentedControlItem />` nodes as children.
   */
  children: _propTypes.default.node.isRequired,

  /**
   * For the uncontrolled component, the defaultItem will set the initial active state.
   * It expects the index of the selected SegmentControlItem.
   */
  defaultItem: _propTypes.default.oneOfType([
    _propTypes.default.number,
    _propTypes.default.string
  ]),

  /**
   * Sets the identifier of the component.
   */
  id: _propTypes.default.string,

  /**
   * Fires when a <SegmentedControlItem /> is selected.
   *
   * @param {object} event - The source event.
   * @param {number} index - The index of the active <SegmentedControlItem />.
   */
  onChange: _propTypes.default.func,

  /**
   * For the controlled component, the selected will control the active state of the component.
   * It expects the index of the selected SegmentControlItem.
   */
  selectedItem: _propTypes.default.oneOfType([
    _propTypes.default.number,
    _propTypes.default.string
  ]),

  /**
   * @internal
   */
  theme: _propTypes.default.shape({
    smMin: _propTypes.default.string.isRequired
  }).isRequired
};
SegmentedControl.defaultProps = {
  'aria-label': undefined,
  defaultItem: undefined,
  id: undefined,
  onChange: undefined,
  selectedItem: undefined
};
SegmentedControl.displayName = 'SegmentedControl';

var _default = (0, _theme.withTheme)(SegmentedControl);

export default _default;
